import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import { LINEUP_FORMATS, lineupTotals, defaultCap, fmtCap, NHL_CAP_MIN } from './poolFx';

// The live database gets position slots from supabase-migrations/2026-09-28_pool_position_slots.sql.
// Until that is applied, create_pool doesn't know the p_slots_* params — detect that and fall back.
const isMissingSlotsSupport = (err) =>
  !!err && /p_slots|function .*create_pool|schema cache|could not find/i.test(err.message || '');

export default function CreatePoolModal({ userId, onClose, onCreated, lang = 'en' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [kind, setKind] = useState('pool');             // pool | season (GM Season: full league vs CPU GMs)
  const [name, setName] = useState('');
  const [format, setFormat] = useState('regular_season');
  const [entryFee, setEntryFee] = useState('50');
  const [maxMembers, setMaxMembers] = useState('8');
  const [pickSeconds, setPickSeconds] = useState('180');
  const [slotsF, setSlotsF] = useState('10');
  const [slotsD, setSlotsD] = useState('4');
  const [slotsG, setSlotsG] = useState('2');
  const [lineupMode, setLineupMode] = useState(false);   // lines + extras, only lines score
  const [lineupFormat, setLineupFormat] = useState('medium');
  const [freeAgency, setFreeAgency] = useState(false);   // drop / sign undrafted players, 2 a month
  const [capMode, setCapMode] = useState('off');         // off | hard | penalty
  const [capInput, setCapInput] = useState('');          // $M typed by the creator; '' = auto (scaled)
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // lineup mode: the format decides how many F / D / G you draft
  const season = kind === 'season';
  const lines = lineupMode || season;              // a season always has lines
  const lt = lineupTotals(lineupFormat);
  const f = lines ? lt.F : parseInt(slotsF, 10);
  const d = lines ? lt.D : parseInt(slotsD, 10);
  const g = lines ? lt.G : parseInt(slotsG, 10);
  const TEAM_SLOTS = 1; // every lineup also drafts exactly one NHL team
  const rosterTotal = (isNaN(f) ? 0 : f) + (isNaN(d) ? 0 : d) + (isNaN(g) ? 0 : g) + TEAM_SLOTS;
  const playerCount = rosterTotal - TEAM_SLOTS;
  const autoCap = defaultCap(playerCount);                        // real cap scaled to the roster
  const capAmount = capInput.trim() === '' ? autoCap : Math.round(parseFloat(capInput) * 1000000);

  const handleCreate = async () => {
    setError('');
    const fee = parseFloat(entryFee);
    const max = parseInt(maxMembers, 10);
    const sec = parseInt(pickSeconds, 10);
    if (!name.trim()) { setError(t.errName); return; }
    if (season) {
      const sec0 = parseInt(pickSeconds, 10);
      if (capMode !== 'off' && (isNaN(capAmount) || capAmount < playerCount * NHL_CAP_MIN)) { setError(t.errCap(fmtCap(playerCount * NHL_CAP_MIN))); return; }
      setSubmitting(true);
      const { data, error: rpcErr } = await supabase.rpc('create_season_pool', {
        p_user_id: userId, p_name: name.trim(), p_lineup_format: lineupFormat, p_pick_seconds: sec0,
        p_cap_mode: capMode === 'off' ? null : capMode, p_cap_amount: capMode === 'off' ? null : capAmount,
      });
      setSubmitting(false);
      if (rpcErr) { setError(/create_season_pool|schema cache|could not find/i.test(rpcErr.message || '') ? t.errSeasonSql : rpcErr.message); return; }
      if (!data?.success) { setError(data?.error || t.errGeneric); return; }
      onCreated?.(data);
      return;
    }
    if (isNaN(fee) || fee < 0) { setError(t.errFee); return; }
    if (isNaN(max) || max < 2 || max > 20) { setError(t.errMax); return; }
    if (isNaN(sec) || sec < 15 || sec > 600) { setError(t.errSec); return; }
    if ([f, d, g].some((n) => isNaN(n) || n < 0) || rosterTotal < 2 || rosterTotal > 30) { setError(t.errSlots); return; }
    if (capMode !== 'off' && (isNaN(capAmount) || capAmount < playerCount * NHL_CAP_MIN)) { setError(t.errCap(fmtCap(playerCount * NHL_CAP_MIN))); return; }

    setSubmitting(true);
    const base = {
      p_user_id: userId,
      p_name: name.trim(),
      p_format: format,
      p_entry_fee: fee,
      p_max_members: max,
      p_roster_size: rosterTotal,
      p_pick_seconds: sec,
    };
    const withSlots = { ...base, p_slots_f: f, p_slots_d: d, p_slots_g: g, p_slots_t: TEAM_SLOTS };
    const withModes = lineupMode ? { ...withSlots, p_lineup_format: lineupFormat } : withSlots;
    const withFa = freeAgency ? { ...withModes, p_free_agency: true } : withModes;
    let { data, error: rpcErr } = await supabase.rpc('create_pool', capMode !== 'off' ? { ...withFa, p_free_agency: freeAgency, p_cap_mode: capMode, p_cap_amount: capAmount } : withFa);
    if (capMode !== 'off' && isMissingSlotsSupport(rpcErr)) {
      // salary cap migration not applied yet: keep the rest
      ({ data, error: rpcErr } = await supabase.rpc('create_pool', withFa));
    }
    if (freeAgency && isMissingSlotsSupport(rpcErr)) {
      ({ data, error: rpcErr } = await supabase.rpc('create_pool', withModes));
    }
    if (lineupMode && isMissingSlotsSupport(rpcErr)) {
      // lineup migration not applied yet: keep the slots, skip the lineup mode
      ({ data, error: rpcErr } = await supabase.rpc('create_pool', withSlots));
    }
    if (isMissingSlotsSupport(rpcErr)) {
      // Database not migrated yet: create it the old way (same roster size, any position).
      ({ data, error: rpcErr } = await supabase.rpc('create_pool', base));
    }
    setSubmitting(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errGeneric); return; }
    onCreated?.(data);
  };

  return (
    <div className="pool-modal-backdrop">{/* only Cancel closes it — a stray click outside must not lose the form */}
      <div className="pool-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{season ? t.titleSeason : t.title}</h3>

        {/* what kind: a regular pool with friends, or a GM Season (full 32-team league vs CPU GMs) */}
        <div className="pool-form-row">
          <div className="pool-formats pool-kinds">
            {['pool', 'season'].map((k) => (
              <button key={k} type="button" className={`pool-format ${kind === k ? 'active' : ''}`} onClick={() => { setKind(k); if (k === 'season' && capMode === 'off') setCapMode('hard'); }}>
                <span className="pool-format-name">{t.kinds[k]}</span>
                <span className="pool-format-lines"><span>{t.kindHints[k]}</span></span>
              </button>
            ))}
          </div>
          {season && <div className="pool-slots-total">{t.seasonRules}</div>}
        </div>

        <div className="pool-form-row">
          <label>{t.name}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.namePlaceholder}
            maxLength={60}
          />
        </div>

        {!season && <div className="pool-form-row">
          <label>{t.format}</label>
          <select value={format} onChange={(e) => setFormat(e.target.value)}>
            <option value="regular_season">{t.formatRegular}</option>
            <option value="playoff">{t.formatPlayoff}</option>
          </select>
        </div>}

        {!season && <div className="pool-form-grid">
          <div className="pool-form-row">
            <label>{t.entryFee} ($)</label>
            <input type="number" min="0" step="1" value={entryFee} onChange={(e) => setEntryFee(e.target.value)} />
          </div>
          <div className="pool-form-row">
            <label>{t.maxMembers}</label>
            <input type="number" min="2" max="20" step="1" value={maxMembers} onChange={(e) => setMaxMembers(e.target.value)} />
          </div>
        </div>}

        {/* Lineup mode: lines + extras, only players in your lines score */}
        {!season && <div className="pool-form-row">
          <label className="pool-toggle-row">
            <span>
              <b>{t.lineupMode}</b>
              <small>{t.lineupModeHint}</small>
            </span>
            <input type="checkbox" role="switch" className="menu-switch" checked={lineupMode} onChange={(e) => setLineupMode(e.target.checked)} />
          </label>
        </div>}

        {lines && (
          <div className="pool-form-row">
            <label>{t.lineupFormat}</label>
            <div className="pool-formats">
              {Object.keys(LINEUP_FORMATS).map((key) => {
                const lines = LINEUP_FORMATS[key];
                const tot = lineupTotals(key);
                return (
                  <button key={key} type="button" className={`pool-format ${lineupFormat === key ? 'active' : ''}`} onClick={() => setLineupFormat(key)}>
                    <span className="pool-format-name">{t.formats[key]}</span>
                    <span className="pool-format-lines">
                      {['F', 'D', 'G'].map((pos) => {
                        const main = lines.filter((x) => x.pos === pos && x.mult > 0);
                        const ext = lines.filter((x) => x.pos === pos && x.mult === 0).reduce((n, x) => n + x.cap, 0);
                        return (
                          <span key={pos}>
                            <b>{pos}</b> {pos === 'G' ? main.length : `${main.length} ${pos === 'F' ? t.lineWord : t.pairWord}`}{ext ? ` + ${ext}` : ''}
                          </span>
                        );
                      })}
                    </span>
                    <span className="pool-format-total">{tot.F + tot.D + tot.G} {t.playersWord}</span>
                  </button>
                );
              })}
            </div>
            <div className="pool-slots-total">{t.lineupScoring}</div>
          </div>
        )}

        <div className="pool-form-row" style={lines ? { display: 'none' } : undefined}>
          <label>{t.lineup}</label>
          <div className="pool-slots">
            {[
              ['F', t.forwards, slotsF, setSlotsF],
              ['D', t.defence, slotsD, setSlotsD],
              ['G', t.goalies, slotsG, setSlotsG],
            ].map(([key, label, value, set]) => (
              <label key={key} className="pool-slot">
                <span className="pool-slot-pos">{key}</span>
                <input type="number" min="0" max="30" step="1" value={value} onChange={(e) => set(e.target.value)} aria-label={label} />
                <span className="pool-slot-label">{label}</span>
              </label>
            ))}
          </div>
          <div className="pool-slots-total">{t.rosterTotal(rosterTotal)}</div>
        </div>

        {/* Free agency: drop one of yours, sign an undrafted player at the same position */}
        {!season && <div className="pool-form-row">
          <label className="pool-toggle-row">
            <span>
              <b>{t.freeAgency}</b>
              <small>{t.freeAgencyHint}</small>
            </span>
            <input type="checkbox" role="switch" className="menu-switch" checked={freeAgency} onChange={(e) => setFreeAgency(e.target.checked)} />
          </label>
        </div>}

        {/* Salary cap: real cap hits, cap scaled to the roster (creator can override) */}
        <div className="pool-form-row">
          <label>{t.salaryCap}</label>
          <div className="pool-formats pool-cap-modes">
            {['off', 'hard', 'penalty'].map((m) => (
              <button key={m} type="button" className={`pool-format ${capMode === m ? 'active' : ''}`} onClick={() => setCapMode(m)}>
                <span className="pool-format-name">{t.capModes[m]}</span>
                <span className="pool-format-lines"><span>{t.capModeHints[m]}</span></span>
              </button>
            ))}
          </div>
          {capMode !== 'off' && <div className="pool-slots-total">{t.capModeRules[capMode]}</div>}
        </div>

        {capMode !== 'off' && (
          <div className="pool-form-row">
            <label>{t.capAmount}</label>
            <div className="pool-cap-field">
              <span className="pool-cap-unit">$</span>
              <input type="number" min="1" step="0.1" value={capInput} placeholder={(autoCap / 1000000).toFixed(1)}
                onChange={(e) => setCapInput(e.target.value)} aria-label={t.capAmount} />
              <span className="pool-cap-unit">M</span>
            </div>
            <div className="pool-slots-total">
              {capInput.trim() === ''
                ? t.capAuto(fmtCap(autoCap), playerCount)
                : <>{t.capCustom(fmtCap(autoCap))} <button type="button" className="pool-cap-reset" onClick={() => setCapInput('')}>{t.capReset}</button></>}
            </div>
          </div>
        )}

        <div className="pool-form-row">
          <label>{t.pickTimer}</label>
          <select value={pickSeconds} onChange={(e) => setPickSeconds(e.target.value)}>
            {[30, 60, 90, 120, 180, 300, 600].map((s) => (
              <option key={s} value={String(s)}>{s < 60 ? `${s} s` : `${s / 60} min`}</option>
            ))}
          </select>
        </div>

        <div className="pool-form-row pool-form-note">{season ? t.seasonNote : t.winnerNote}</div>

        {error && <div className="pool-form-error">{error}</div>}

        <div className="pool-form-actions">
          <button className="pool-btn-cancel" onClick={onClose} disabled={submitting}>
            {t.cancel}
          </button>
          <button className="pool-btn-confirm" onClick={handleCreate} disabled={submitting}>
            {submitting ? t.creating : t.create}
          </button>
        </div>
      </div>
    </div>
  );
}

const TR = {
  en: {
    title: 'Create Hockey Pool',
    titleSeason: 'Create a GM Season',
    kinds: { pool: 'Pool', season: 'GM Season' },
    kindHints: { pool: 'You and your friends', season: 'Full league vs CPU GMs' },
    seasonRules: 'All 32 NHL teams, always. Friends can join; CPU GMs fill every empty seat when you start. Everyone drafts an NHL team (its real 84-game schedule), then players — at least 3 from your own team. Every real game = you vs the member who has the other team: all the points your lineup made since your last game, higher score wins.',
    seasonNote: 'Free. Best record wins the league.',
    errSeasonSql: 'GM Season needs the database update first (2026-09-30_solo_season.sql).',
    name: 'Pool Name',
    namePlaceholder: 'e.g., Friday Night Draft',
    format: 'Format',
    formatRegular: 'Regular Season',
    formatPlayoff: 'Playoffs',
    entryFee: 'Entry Fee',
    maxMembers: 'Max Members (2-20)',
    lineup: 'Lineup',
    forwards: 'Forwards',
    defence: 'Defence',
    goalies: 'Goalies',
    team: 'NHL team',
    lineupMode: 'Lineup mode',
    lineupModeHint: 'Set your lines every day. Only your lines score; extras score 0.',
    lineupFormat: 'Lineup size',
    formats: { small: 'Small', medium: 'Medium', large: 'Large', full: 'Full' },
    lineWord: 'lines', pairWord: 'pairs', playersWord: 'players',
    lineupScoring: 'Line 1 and Pair 1 score ×2 · other lines ×1 · extras 0 · only the starting goalie scores',
    teamHint: 'Everyone also drafts one NHL team — it scores its standings points',
    salaryCap: 'Salary cap',
    capModes: { off: 'No cap', hard: 'Hard cap', penalty: 'Penalties' },
    capModeHints: { off: 'Salaries don’t count', hard: 'Never go over', penalty: 'Over it costs you' },
    capModeRules: {
      hard: 'Every pick and pickup must fit under the cap.',
      penalty: 'You can go over. While over: Line 1 and Pair 1 don’t score double, and only 1 free-agent pickup a month.',
    },
    capAmount: 'Cap amount',
    capAuto: (c, n) => `Auto: ${c}, the real $104M cap scaled to ${n} players. Type an amount to change it.`,
    capCustom: (c) => `Custom cap (auto would be ${c}).`,
    capReset: 'Use auto',
    errCap: (min) => `The cap must be at least ${min} (every player at the league minimum)`,
    freeAgency: 'Free agency',
    freeAgencyHint: 'Drop a player and sign an undrafted one at the same position. 2 pickups a month. New players only score from the day you sign them.',
    rosterTotal: (n) => `${n - 1} players per team + your NHL team`,
    pickTimer: 'Time per pick',
    winnerNote: 'Winner takes all.',
    cancel: 'Cancel',
    create: 'Create & Join',
    creating: 'Creating…',
    errName: 'Pool name is required',
    errFee: 'Entry fee must be 0 or higher',
    errMax: 'Max members must be between 2 and 20',
    errSec: 'Pick timer must be between 15 and 600 seconds',
    errSlots: 'Lineup must have 1 to 30 players (forwards + defence + goalies)',
    errGeneric: 'Could not create pool',
  },
  fr: {
    title: 'Créer un pool de hockey',
    titleSeason: 'Créer une saison DG',
    kinds: { pool: 'Pool', season: 'Saison DG' },
    kindHints: { pool: 'Toi et tes amis', season: 'Ligue complète contre des DG CPU' },
    seasonRules: 'Les 32 équipes de la LNH, toujours. Tes amis peuvent se joindre; des DG CPU prennent chaque place libre au départ. Chacun repêche une équipe LNH (son vrai calendrier de 84 matchs), puis des joueurs — au moins 3 de ta propre équipe. Chaque vrai match = toi contre le membre qui a l’autre équipe : tous les points de ton alignement depuis ton dernier match, le plus haut gagne.',
    seasonNote: 'Gratuit. La meilleure fiche gagne la ligue.',
    errSeasonSql: 'La saison DG a besoin de la mise à jour de la base de données (2026-09-30_solo_season.sql).',
    name: 'Nom du pool',
    namePlaceholder: 'ex. Repêchage du vendredi',
    format: 'Format',
    formatRegular: 'Saison régulière',
    formatPlayoff: 'Séries éliminatoires',
    entryFee: "Frais d'inscription",
    maxMembers: 'Joueurs max (2-20)',
    lineup: 'Alignement',
    forwards: 'Attaquants',
    defence: 'Défenseurs',
    goalies: 'Gardiens',
    team: 'Équipe LNH',
    lineupMode: 'Mode alignement',
    lineupModeHint: 'Ajuste tes trios chaque jour. Seuls tes trios marquent; les réserves marquent 0.',
    lineupFormat: 'Taille de l’alignement',
    formats: { small: 'Petit', medium: 'Moyen', large: 'Grand', full: 'Complet' },
    lineWord: 'trios', pairWord: 'paires', playersWord: 'joueurs',
    lineupScoring: '1er trio et 1re paire ×2 · autres trios ×1 · réserves 0 · seul le gardien partant marque',
    teamHint: 'Chacun repêche aussi une équipe LNH — elle marque ses points au classement',
    salaryCap: 'Plafond salarial',
    capModes: { off: 'Aucun plafond', hard: 'Plafond strict', penalty: 'Pénalités' },
    capModeHints: { off: 'Salaires ignorés', hard: 'Jamais au-dessus', penalty: 'Dépasser coûte cher' },
    capModeRules: {
      hard: 'Chaque choix et chaque signature doit rentrer sous le plafond.',
      penalty: 'Tu peux dépasser. Au-dessus : le 1er trio et la 1re paire ne comptent pas double, et 1 seule signature par mois.',
    },
    capAmount: 'Montant du plafond',
    capAuto: (c, n) => `Auto : ${c}, le vrai plafond de 104 M$ ramené à ${n} joueurs. Tape un montant pour le changer.`,
    capCustom: (c) => `Plafond personnalisé (auto : ${c}).`,
    capReset: 'Revenir à l’auto',
    errCap: (min) => `Le plafond doit être d’au moins ${min} (chaque joueur au salaire minimum)`,
    freeAgency: 'Joueurs autonomes',
    freeAgencyHint: 'Libère un joueur et signe un joueur non repêché à la même position. 2 signatures par mois. Un nouveau joueur marque seulement à partir du jour où tu le signes.',
    rosterTotal: (n) => `${n - 1} joueurs par équipe + ton équipe LNH`,
    pickTimer: 'Temps par choix',
    winnerNote: 'Le gagnant rafle tout.',
    cancel: 'Annuler',
    create: 'Créer et rejoindre',
    creating: 'Création…',
    errName: 'Le nom de la poule est requis',
    errFee: 'Les frais doivent être de 0 ou plus',
    errMax: 'Le nombre de joueurs doit être entre 2 et 20',
    errSec: 'Le minuteur doit être entre 15 et 600 secondes',
    errSlots: "L'alignement doit avoir de 1 à 30 joueurs (attaquants + défenseurs + gardiens)",
    errGeneric: 'Impossible de créer le pool',
  },
};
